import type { EvidenceStatus } from '../coverage.js';

/**
 * The process map model (ADR-017 §1, E12-F09 §2). A map version is data:
 * lanes (roles, in row order), steps and flows, each with a key that stays
 * the same across versions, so a step keeps its identity, citations and
 * provenance. A map is never stored as XML or coordinates: code lays it out
 * (`layoutMap`) and writes BPMN 2.0 from it (`toBpmnXml`).
 *
 * The same model holds engagement maps, the framework's reference processes
 * and future-state maps per vendor product (E13, ADR-020).
 */

export const STEP_TYPES = [
  'start_event',
  'end_event',
  'task',
  'user_task',
  'manual_task',
  'service_task',
  'exclusive_gateway',
] as const;
export type StepType = (typeof STEP_TYPES)[number];

/** Steps that do work. Only these need a citation; events and decisions do not. */
export const TASK_TYPES: readonly StepType[] = ['task', 'user_task', 'manual_task', 'service_task'];

/** Who added a step: AI drafting, map chat, the side panel or the canvas (rule 27). */
export const STEP_ORIGINS = ['ai_draft', 'chat', 'manual', 'canvas'] as const;
export type StepOrigin = (typeof STEP_ORIGINS)[number];

export interface MapLane {
  key: string;
  name: string;
  /** A lane for a system rather than a role (future-state maps). Drawn like any lane. */
  is_system?: boolean;
}

/** A citation of a discovery evidence item. Its status is read with the version. */
export interface EvidenceCitation {
  kind: 'discovery_evidence';
  evidence_id: string;
  /** `E-010`. For display and the BPMN documentation. */
  evidence_number: string | null;
  /** The item's status now. A withdrawn item stays cited and shows withdrawn (Decision 54.20). */
  evidence_status: EvidenceStatus;
}

/** A citation of the client's validation of a version (Decision 53.8). */
export interface ValidationCitation {
  kind: 'client_validation';
  validation_id: string;
  /** Who validated and when (ISO date), for display and the BPMN documentation. */
  validated_by: string | null;
  validated_at: string | null;
}

/** The kinds of vendor knowledge item (E13 Decision 56, E13-design §9 item 24). */
export const KNOWLEDGE_ITEM_KINDS = [
  'capability',
  'limitation',
  'differentiator',
  'requirement_response',
  'integration',
  'hosting_security',
  'company',
  'overview',
] as const;
export type KnowledgeItemKind = (typeof KNOWLEDGE_ITEM_KINDS)[number];

/** A knowledge item's status. A map cites confirmed items; a pinned one may have moved on since. */
export type KnowledgeItemStatus =
  | 'proposed'
  | 'confirmed'
  | 'flagged'
  | 'superseded'
  | 'retired'
  | 'declined';

/**
 * A future-state step's citation of one vendor knowledge item (ADR-020 §1,
 * §2). The id pins one immutable version of the vendor's claim: superseding
 * never edits an item, so what a version cites never changes under it
 * (rule 25). The rest is read with the version, the status as it is now.
 */
export interface KnowledgeCitation {
  kind: 'vendor_knowledge';
  item_id: string;
  /** `VK-00412`. */
  item_number: string | null;
  item_kind: KnowledgeItemKind;
  item_status: KnowledgeItemStatus;
  /** A requirement response's answer code (S, F, C, T, N); null for other kinds. */
  answer_code: string | null;
  /** The library item a requirement response answers; null for other kinds. */
  library_item_id: string | null;
}

export type StepCitation = EvidenceCitation | ValidationCitation | KnowledgeCitation;

/** The id of the record a citation names, whatever its kind. */
export function citedId(c: StepCitation): string {
  return c.kind === 'discovery_evidence'
    ? c.evidence_id
    : c.kind === 'client_validation'
      ? c.validation_id
      : c.item_id;
}

export interface StepPainPoint {
  pain_point_id: string;
  /** `PP-04`. */
  number: string | null;
}

export interface MapStep {
  key: string;
  type: StepType;
  /** The lane's key. */
  lane: string;
  name: string;
  /** The system the step is done in, if any (`system_name`). */
  system: string | null;
  origin: StepOrigin;
  origin_user_id: string | null;
  /** The chat message that added the step (chat only). */
  origin_message_id: string | null;
  citations: StepCitation[];
  pain_points: StepPainPoint[];
  /**
   * Future-state maps only (rule 24): the step says the product does
   * something without citing a knowledge item. Absent means false; it is
   * never set together with a `vendor_knowledge` citation.
   */
  is_assumption?: boolean;
}

export interface MapFlow {
  key: string;
  /** Step keys. */
  from: string;
  to: string;
  label: string | null;
  /** An exception branch. Left out of the main path, the metrics and the column layering. */
  is_exception: boolean;
}

export interface MapModel {
  /** Top to bottom. The order gives each lane its row. */
  lanes: MapLane[];
  /**
   * In `sequence` order. The order is only a tie-break inside a lane and
   * column, never a coordinate: flows decide the columns.
   */
  steps: MapStep[];
  flows: MapFlow[];
}

/** Keys become part of BPMN element ids, so they stay to letters, digits, `_` and `-`. */
export const KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

/** Lane, step and system names, and flow labels, as the database allows them. */
export const MAX_NAME_LENGTH = 200;
export const MAX_LABEL_LENGTH = 200;

export function isStepType(value: unknown): value is StepType {
  return typeof value === 'string' && (STEP_TYPES as readonly string[]).includes(value);
}

export function isTaskType(type: StepType): boolean {
  return TASK_TYPES.includes(type);
}

/** Steps people do: every task the system does not do on its own. */
export function isPeopleTask(type: StepType): boolean {
  return isTaskType(type) && type !== 'service_task';
}

/** Trim, turn control characters and runs of whitespace into single spaces. */
export function cleanText(value: string): string {
  return value.replace(/[\u0000-\u001f\u007f\s]+/g, ' ').trim();
}

/** A deep copy that shares nothing with the original. */
export function cloneModel(model: MapModel): MapModel {
  return structuredClone(model);
}

/** The smallest `<prefix><n>` (n from 1) that is not taken. Deterministic for a given model. */
export function nextFreeKey(prefix: string, taken: ReadonlySet<string>): string {
  for (let n = 1; ; n += 1) {
    const key = `${prefix}${n}`;
    if (!taken.has(key)) return key;
  }
}

export function stepsByKey(model: MapModel): Map<string, MapStep> {
  return new Map(model.steps.map((s) => [s.key, s]));
}

/** `“name”` — how steps, lanes and labels are quoted in messages and summaries. */
export function quote(text: string): string {
  return `“${text}”`;
}

/** A step quoted by its name, or by its key when it has none (an unnamed event or decision). */
export function stepLabel(step: Pick<MapStep, 'key' | 'name'>): string {
  const name = typeof step.name === 'string' ? step.name.trim() : '';
  return quote(name || step.key);
}
